import type { Chain, ChainStep } from "./types";

/**
 * Parse a chain cell value. Accepts 0.4, 40, "40%", "40%[20%]" (services [manufacturing], rule 3).
 * Numbers above 1 are read as percentages.
 */
export function parseCum(v: unknown): { cum: number; mfgCum?: number } | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return isFinite(v) ? { cum: v > 1 ? v / 100 : v } : null;
  const s = String(v).trim();
  const m = s.match(/^(-?\d+(?:\.\d+)?)\s*(%?)\s*(?:\[\s*(-?\d+(?:\.\d+)?)\s*(%?)\s*\])?$/);
  if (!m) return null;
  const toFrac = (num: string, pct: string) => {
    const n = Number(num);
    return pct || n > 1 ? n / 100 : n;
  };
  const out: { cum: number; mfgCum?: number } = { cum: toFrac(m[1], m[2]) };
  if (m[3] !== undefined) out.mfgCum = toFrac(m[3], m[4]);
  return out;
}

export function formatCum(s: ChainStep): string {
  const p = (x: number) => `${+(x * 100).toFixed(4)}%`;
  return s.mfgCum === undefined ? p(s.cum) : `${p(s.cum)}[${p(s.mfgCum)}]`;
}

export type ChainIssue =
  | { kind: "empty" }
  | { kind: "notMonotonic"; step: number }
  | { kind: "outOfRange"; step: number }
  | { kind: "lastNot100" }
  | { kind: "duplicateMilestone"; step: number; milestone: string }
  | { kind: "unknownMilestone"; step: number; milestone: string };

const EPS = 1e-9;

/** Chains are cumulative, never decreasing, and end at 100% (rules 2, 3). */
export function checkChain(chain: Chain, knownMilestones?: Set<string>): ChainIssue[] {
  const issues: ChainIssue[] = [];
  const st = chain.steps;
  if (!st.length) return [{ kind: "empty" }];
  const seen = new Set<string>();
  st.forEach((s, i) => {
    if (s.cum < -EPS || s.cum > 1 + EPS) issues.push({ kind: "outOfRange", step: i + 1 });
    if (i > 0 && s.cum < st[i - 1].cum - EPS) issues.push({ kind: "notMonotonic", step: i + 1 });
    if (s.mfgCum !== undefined && i > 0 && st[i - 1].mfgCum !== undefined && s.mfgCum < st[i - 1].mfgCum! - EPS)
      issues.push({ kind: "notMonotonic", step: i + 1 });
    if (seen.has(s.milestone)) issues.push({ kind: "duplicateMilestone", step: i + 1, milestone: s.milestone });
    seen.add(s.milestone);
    if (knownMilestones && !knownMilestones.has(s.milestone))
      issues.push({ kind: "unknownMilestone", step: i + 1, milestone: s.milestone });
  });
  if (Math.abs(st[st.length - 1].cum - 1) > EPS) issues.push({ kind: "lastNot100" });
  return issues;
}

/** Share of the deliverable weight carried by each step: cum_j - cum_(j-1). */
export function stepShares(steps: { cum: number }[]): number[] {
  // Rounded to 1e-12 so 0.2 - 0.05 is 0.15, not 0.15000000000000002.
  return steps.map((s, i) => Math.round((s.cum - (i > 0 ? steps[i - 1].cum : 0)) * 1e12) / 1e12);
}
